import React, { useEffect, useMemo, useState } from 'react';
import RecordWizard from '../reusable/formRecords/RecordWizards';
import FileUploadField from '../shared/FileUploadField';
import {
  Field as FormField,
  Select as FormSelect,
  ReadonlyBox,
  SectionHead,
  SectionDivider,
  INP_LOCKED,
  CurrencyInput,
  isCurrencyLabel,
  INP,
} from '../reusable/formRecords/formFieldPrimitives';
import { useRegistryFieldProps, useRegistryLabel } from '../Admin/FoundationalTables/FormRegistry/registryFieldOverrides';
import { MultiToggle, SingleToggle, MultiSelectPicklist } from '../shared/forms/FormControls';
import { injectChoiceOption } from './planningOptionUtils';
import {
  ACTIVITY_REPORT_STATUSES,
  WORKPLAN_ACTIVITY_CATEGORIES,
  WORKPLAN_BENEFICIARY_CLASSES,
  WORKPLAN_DEPARTMENTS,
  WORKPLAN_GEO_SCOPES,
  WORKPLAN_PLAN_CATEGORIES,
  WORKPLAN_PLAN_TYPES,
  WORKPLAN_REPORT_TYPES,
  WORKPLAN_STATUSES,
  formatDateInput,
  workplanActivitiesApi,
} from '../../api/planningApi';
import { PPP_TYPE_OPTIONS, PPP_ARRANGEMENT_MODEL_OPTIONS, PPP_STATUS_OPTIONS, pppArrangementsApi } from '../../api/pppArrangementsApi';
import { cooperativesApi, COOP_TYPE_MAP } from '../../api/workforceApi';
import { facilitiesApi } from '../../api/facilitiesApi';
import { useApplyScopeLock, useLocationOptions } from '../Admin/shared/useLocationOptions';
import ProposeActorModal from '../Workforce/ProposeActorModal';
import ProposeFacilityModal from '../Infrastructure/shared/ProposeFacilityModal';
import ProposeClimateAssessmentModal from '../Infrastructure/shared/ProposeClimateAssessmentModal';
import { logRegistryProposal } from '../../api/registryCentralApi';
import { useAuth } from '../../context/AuthContext';
import { approvedClimateAssessments } from '../../api/facilityStatusUpdatesApi';
import { CLIMATE_SCREENING_OPTIONS, EMISSIONS_CLASSIFICATION_OPTIONS, labelOf } from '../Infrastructure/shared/climateOptions';

const VIEW_STATUS_DOT = {
  draft: 'bg-gray-400',
  submitted: 'bg-blue-500',
  validated: 'bg-amber-500',
  approved: 'bg-green-500',
  rejected: 'bg-red-500',
};

const VIEW_STATUS_PILL = {
  draft: 'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-50 text-blue-700',
  validated: 'bg-amber-50 text-amber-700',
  approved: 'bg-green-50 text-green-700',
  rejected: 'bg-red-50 text-red-600',
};

const REFORM_CATEGORY_OPTIONS = [
  { value: 'policy_reform', label: 'Policy Reform' },
  { value: 'process_improvement', label: 'Process Improvement' },
  { value: 'digital_transformation', label: 'Digital Transformation' },
  { value: 'service_delivery_improvement', label: 'Service Delivery Improvement' },
  { value: 'organisational_reform', label: 'Organisational Reform' },
];

// Reporting Period on the Activity Report is captured the same way as
// Livestock Population's Reference Period — a Period Type, then a Period
// Instance list generated for that type — instead of one flat dropdown.
const REPORT_PERIOD_TYPE_OPTIONS = ['Monthly', 'Quarterly', 'Annual'];
const REPORT_PERIOD_YEARS = [2024, 2025, 2026, 2027];

function reportPeriodInstances(type) {
  switch (type) {
    case 'Monthly':
      return REPORT_PERIOD_YEARS.flatMap((y) => Array.from({ length: 12 }, (_, i) => `${y}-${String(i + 1).padStart(2, '0')}`));
    case 'Quarterly':
      return REPORT_PERIOD_YEARS.flatMap((y) => ['Q1', 'Q2', 'Q3', 'Q4'].map((q) => `${y}-${q}`));
    case 'Annual':
      return REPORT_PERIOD_YEARS.map(String);
    default:
      return [];
  }
}

function guessReportPeriodType(value) {
  if (!value) return '';
  if (/-Q\d$/.test(value)) return 'Quarterly';
  if (/-\d{2}$/.test(value)) return 'Monthly';
  if (/^\d{4}$/.test(value)) return 'Annual';
  return 'Quarterly';
}

function extractApiError(err, fallback) {
  const resp = err?.response?.data;
  const fieldErrors = resp?.errors;
  if (fieldErrors && typeof fieldErrors === 'object') {
    const msgs = Object.entries(fieldErrors)
      .flatMap(([, value]) => (Array.isArray(value) ? value : [String(value)]))
      .filter(Boolean)
      .slice(0, 3);
    if (msgs.length) return msgs.join(' | ');
  }
  return resp?.message || err?.message || fallback || 'Failed to save.';
}

function currentChoice(value, label) {
  const text = String(value ?? '').trim();
  if (!text) return null;
  return { value: text, label: String(label ?? text).trim() || text };
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function ModalOverlay({ onClose, inline = false, children }) {
  if (inline) {
    return <div className="w-full">{children}</div>;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto" onClick={(event) => event.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function FieldContainer({ label, required = false, hint, span2 = false, children }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <FormField label={label} required={required} hint={hint}>
        {children}
      </FormField>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder: pagePlaceholder = '', type = 'text', required = false, hint, readOnly = false, span2 = false, min, max }) {
  const { placeholder } = useRegistryFieldProps(label, { placeholder: pagePlaceholder });
  if (type === 'number' && isCurrencyLabel(label)) {
    return (
      <FieldContainer label={label} required={required} hint={hint} span2={span2}>
        <CurrencyInput value={value} onChange={onChange} placeholder={placeholder || '0'} readOnly={readOnly} min={min ?? '0'} />
      </FieldContainer>
    );
  }
  return (
    <FieldContainer label={label} required={required} hint={hint} span2={span2}>
      <input
        type={type}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        min={min}
        max={max}
        className={`${INP} ${readOnly ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
      />
    </FieldContainer>
  );
}

function TextAreaField({ label, value, onChange, placeholder: pagePlaceholder = '', rows = 3, required = false, hint, span2 = false }) {
  const { placeholder } = useRegistryFieldProps(label, { placeholder: pagePlaceholder });
  return (
    <FieldContainer label={label} required={required} hint={hint} span2={span2}>
      <textarea
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className={`${INP} resize-none`}
      />
    </FieldContainer>
  );
}

function SelectField({ label, value, onChange, options = [], placeholder = 'Select...', required = false, hint, span2 = false, disabled = false }) {
  return (
    <FieldContainer label={label} required={required} hint={hint} span2={span2}>
      <FormSelect
        value={value ?? ''}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        className={INP}
        disabled={disabled}
      />
    </FieldContainer>
  );
}

function RegistryField({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  fallbackPlaceholder = 'Enter value',
  required = false,
  hint,
  span2 = false,
  onPropose,
  disabled = false,
}) {
  const hasOptions = Array.isArray(options) && options.length > 0;
  const selectNode = (
    <FormSelect
      value={value ?? ''}
      onChange={onChange}
      options={hasOptions ? options : []}
      placeholder={hasOptions ? placeholder : fallbackPlaceholder}
      className={disabled || !hasOptions ? INP_LOCKED : INP}
      disabled={disabled || !hasOptions}
    />
  );

  return (
    <FieldContainer label={label} required={required} hint={hint} span2={span2}>
      {onPropose && !disabled ? (
        <div className="flex gap-2">
          <div className="flex-1">{selectNode}</div>
          <button
            type="button"
            onClick={onPropose}
            className="shrink-0 rounded-lg border border-dashed border-green-300 bg-green-50 px-4 py-3 text-[12px] font-semibold text-green-700 hover:bg-green-100"
          >
            + Propose
          </button>
        </div>
      ) : selectNode}
    </FieldContainer>
  );
}

function ToggleField({ label, value, onChange, options = [], required = false, hint, span2 = false, placeholder = 'Select' }) {
  return (
    <FieldContainer label={label} required={required} hint={hint} span2={span2}>
      <FormSelect value={value ?? ''} onChange={(event) => onChange(event.target.value)} options={options} placeholder={placeholder} className={INP} />
    </FieldContainer>
  );
}

// Two-choice selector shown as buttons (e.g. Plan Category: Workplan / Reform Plan).
function ChoiceButtonsField({ label, value, onChange, options = [], required = false, hint, span2 = false }) {
  return (
    <FieldContainer label={label} required={required} hint={hint} span2={span2}>
      <SingleToggle value={value ?? ''} onChange={(next) => next && onChange(next)} options={options} />
    </FieldContainer>
  );
}

function MultiField({ label, values = [], onChange, options = [], required = false, hint, span2 = false }) {
  return (
    <FieldContainer label={label} required={required} hint={hint} span2={span2}>
      <MultiToggle values={Array.isArray(values) ? values : []} onChange={onChange} options={options} />
    </FieldContainer>
  );
}

function ReviewRow({ label: pageLabel, value }) {
  const label = useRegistryLabel(pageLabel);
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs font-semibold text-gray-500">{label}</span>
      <span className="text-sm text-right text-gray-900">{value || value === 0 ? String(value) : '—'}</span>
    </div>
  );
}

// RegistryField dropdowns store the option's uuid as the field value —
// resolve it back to the option's human-readable label for display in a
// review step, instead of showing the raw uuid the user never actually
// typed or saw.
function resolveLabel(options, value) {
  if (!value) return value;
  const match = (options ?? []).find((option) => String(option.value) === String(value));
  return match?.label ?? value;
}

// A blank row for the repeatable "Activity" section embedded in the Workplan
// Registration wizard (DCIC-11 v2.0 — workplan header + repeatable activity
// rows is one instrument, not two disconnected forms).
function emptyActivityEntry() {
  return {
    activityTitle: '',
    category: '',
    budgetNgn: '',
    targetBeneficiaries: '',
    beneficiaryClass: '',
    // v2.2 climate — default not_applicable; required needs a target facility.
    climateScreening: 'not_applicable',
    targetFacilityId: '',
  };
}

// Registry-first facility list for the activity's Target Facility lookup.
function useFacilityOptions() {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    let active = true;
    facilitiesApi.listAll({ per_page: 500 })
      .then(({ items }) => {
        if (!active) return;
        setOptions((items ?? []).map((f) => ({ value: f.uuid ?? f.id, label: [f.name, f.facilityType, f.lga].filter(Boolean).join(' — ') })));
      })
      .catch(() => { if (active) setOptions([]); });
    return () => { active = false; };
  }, []);
  return [options, setOptions];
}

function WorkplanActivitiesRepeatable({ entries, onChange, categoryOptions, beneficiaryClassOptions, remainingPool }) {
  const [facilityOptions, setFacilityOptions] = useFacilityOptions();
  const [proposeFor, setProposeFor] = useState(null);
  const updateEntry = (index, updates) => {
    onChange(entries.map((entry, i) => (i === index ? { ...entry, ...updates } : entry)));
  };
  const removeEntry = (index) => {
    onChange(entries.filter((_, i) => i !== index));
  };
  const addEntry = () => {
    onChange([...entries, emptyActivityEntry()]);
  };
  const committed = entries.reduce((sum, entry) => sum + (Number(entry.budgetNgn) || 0), 0);

  return (
    <section className="space-y-3">
      <SectionHead title="Linked Activity" />
      {remainingPool !== null && (
        <p className={`text-xs ${committed > remainingPool ? 'text-red-500' : 'text-gray-400'}`}>
          Remaining budget for new activities: ₦{Math.max(remainingPool, 0).toLocaleString()}
          {committed > remainingPool ? ' — combined activity budget exceeds this.' : ''}
        </p>
      )}

      {entries.map((entry, index) => (
        <div key={index} className="relative space-y-3 rounded-lg border border-gray-100 bg-white p-4">
          <button
            type="button"
            onClick={() => removeEntry(index)}
            className="absolute right-3 top-3 text-xs font-semibold text-red-600 hover:text-red-700"
          >
            Remove
          </button>
          <div className="grid grid-cols-2 gap-4 pr-16">
            <TextField
              label="Activity Title"
              value={entry.activityTitle}
              onChange={(event) => updateEntry(index, { activityTitle: event.target.value })}
              placeholder="Activity title"
              hint="Minimum 5 characters"
              required
            />
            <SelectField
              label="Category"
              value={entry.category}
              onChange={(event) => updateEntry(index, { category: event.target.value })}
              options={categoryOptions}
              placeholder="Select category"
              required
            />
            <div className="col-span-2 grid grid-cols-3 gap-4">
              <TextField
                label="Budget (NGN)"
                type="number"
                value={entry.budgetNgn}
                onChange={(event) => updateEntry(index, { budgetNgn: event.target.value })}
                placeholder="0"
                required
              />
              <SelectField
                label="Beneficiary Class"
                value={entry.beneficiaryClass}
                onChange={(event) => updateEntry(index, { beneficiaryClass: event.target.value })}
                options={beneficiaryClassOptions}
                placeholder="Select (optional)"
              />
              <TextField
                label="Target Beneficiaries"
                type="number"
                value={entry.targetBeneficiaries}
                onChange={(event) => updateEntry(index, { targetBeneficiaries: event.target.value })}
                placeholder="0"
                required
              />
            </div>
            <ToggleField
              label="Climate Screening"
              value={entry.climateScreening}
              onChange={(value) => updateEntry(index, {
                climateScreening: value || 'not_applicable',
                targetFacilityId: value === 'required' ? entry.targetFacilityId : '',
              })}
              options={CLIMATE_SCREENING_OPTIONS}
              hint="Required = this intervention is subject to emissions screening on a facility."
              required
              span2
            />
            {entry.climateScreening === 'required' && (
              <RegistryField
                label="Target Facility"
                value={entry.targetFacilityId}
                onChange={(event) => updateEntry(index, { targetFacilityId: event.target.value })}
                options={facilityOptions}
                placeholder="Select from registry"
                fallbackPlaceholder="No facilities in the registry yet"
                hint="The facility this intervention is assessed on."
                onPropose={() => setProposeFor(index)}
                required
                span2
              />
            )}
          </div>
        </div>
      ))}
      <ProposeFacilityModal
        isOpen={proposeFor !== null}
        onClose={() => setProposeFor(null)}
        onCreated={(record) => {
          const uuid = record?.uuid ?? record?.id;
          if (!uuid || proposeFor === null) return;
          setFacilityOptions((prev) => [...prev, { value: uuid, label: record?.name ?? 'New facility' }]);
          updateEntry(proposeFor, { targetFacilityId: uuid });
        }}
      />

      <button
        type="button"
        onClick={addEntry}
        className="w-full rounded-xl border-2 border-dashed border-green-300 bg-green-50 py-3 text-xs font-bold text-green-600 transition-colors hover:bg-green-100"
      >
        + Add Activity
      </button>
    </section>
  );
}

function ActivitiesReviewList({ existingActivities = [], newActivities = [] }) {
  if (existingActivities.length === 0 && newActivities.length === 0) return null;
  return (
    <>
      <SectionHead title="Linked Activity" />
      {existingActivities.map((activity) => (
        <ReviewRow
          key={activity.uuid ?? activity.id}
          label={activity.activityTitle ?? activity.title ?? 'Activity'}
          value={`${activity.category ?? ''} — ₦${Number(activity.budgetNgn ?? activity.budget_ngn ?? 0).toLocaleString()}`}
        />
      ))}
      {newActivities.map((entry, index) => (
        <ReviewRow
          key={`new-${index}`}
          label={entry.activityTitle || `New Activity ${index + 1}`}
          value={[
            entry.category,
            entry.budgetNgn ? `₦${Number(entry.budgetNgn).toLocaleString()}` : '',
            entry.targetBeneficiaries ? `${entry.targetBeneficiaries} beneficiaries` : '',
            entry.climateScreening === 'required' ? 'Climate screening required' : '',
          ].filter(Boolean).join(' — ')}
        />
      ))}
    </>
  );
}

function makeReviewFooter({ onBack, onSubmit, submitting, submitLabel }) {
  return (
    <>
      <button
        type="button"
        onClick={onBack}
        disabled={submitting}
        className="h-11 flex-1 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
      >
        Previous
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
      >
        {submitting ? 'Saving...' : submitLabel}
      </button>
    </>
  );
}

function WorkplanForm({ onSubmit, onClose, context = {}, modulePanel = null, mode = 'create', row = null, onEdit, canEdit }) {
  const referenceOptions = context.referenceOptions ?? {};
  const currentUser = currentChoice(
    context.currentUserUuid ?? context.userUuid,
    context.currentUserLabel ?? context.userName
  );

  const [proposedActor, setProposedActor] = useState(null);
  const [proposeActorOpen, setProposeActorOpen] = useState(false);
  const actorOptions = useMemo(
    () => injectChoiceOption(injectChoiceOption(referenceOptions.actorOptions ?? [], currentUser), proposedActor),
    [referenceOptions.actorOptions, currentUser, proposedActor]
  );

  const [form, setForm] = useState({
    department: '',
    planTitle: '',
    planType: '',
    planCategory: 'Workplan',
    startDate: '',
    endDate: '',
    budgetNgn: '',
    planOwnerUuid: currentUser?.value ?? '',
    geographicScope: '',
    status: 'Draft',
    completionPercent: '',
    reformCategory: '',
    supportingDepartments: [],
    // New activity rows to create alongside this workplan — a fresh draft
    // starts with one blank row since DCIC-11 requires ≥1 activity.
    activities: mode === 'create' ? [emptyActivityEntry()] : [],
  });
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const REVIEW_STEP = 3;
  const isReviewStep = step === REVIEW_STEP;

  useEffect(() => {
    if (!row) return;
    setForm((prev) => ({
      ...prev,
      department: row.department ?? '',
      planTitle: row.planTitle ?? row.title ?? '',
      planType: row.planType ?? row.plan_type ?? '',
      planCategory: row.planCategory ?? row.plan_category ?? 'Workplan',
      startDate: formatDateInput(row.startDate ?? row.start_date ?? ''),
      endDate: formatDateInput(row.endDate ?? row.end_date ?? ''),
      budgetNgn: String(row.budgetNgn ?? row.budget_ngn ?? row.budget ?? ''),
      planOwnerUuid: row.planOwnerUuid ?? row.plan_owner_uuid ?? currentUser?.value ?? '',
      geographicScope: row.geographicScope ?? row.geographic_scope ?? '',
      status: row.status ?? row.workplan_status ?? 'Draft',
      completionPercent: row.completionPercent ?? row.completion_percent ?? '',
      reformCategory: row.reformCategory ?? row.reform_category ?? '',
      supportingDepartments: Array.isArray(row.supportingDepartments)
        ? row.supportingDepartments
        : (Array.isArray(row.supporting_departments) ? row.supporting_departments : []),
    }));
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  // Activities already linked to this workplan (edit mode only) — shown
  // read-only; new rows added in this session go through `form.activities`.
  const existingActivities = useMemo(() => {
    if (!row) return [];
    const activities = context.activities ?? [];
    const workplanId = row.uuid ?? row.id;
    return activities.filter((a) => (a.workplanUuid ?? a.workplan_uuid) === workplanId);
  }, [context.activities, row]);

  const existingActivityBudget = useMemo(
    () => existingActivities.reduce((sum, a) => sum + (Number(a.budgetNgn ?? a.budget_ngn ?? 0) || 0), 0),
    [existingActivities]
  );

  const remainingActivityPool = form.budgetNgn !== ''
    ? (Number(form.budgetNgn) || 0) - existingActivityBudget
    : null;

  const newActivitiesBudget = form.activities.reduce((sum, a) => sum + (Number(a.budgetNgn) || 0), 0);
  const isActivityBudgetValid = remainingActivityPool === null || newActivitiesBudget <= remainingActivityPool;
  const areActivitiesValid = form.activities.every((a) => (
    a.activityTitle.trim().length >= 5 && Boolean(a.category) && Number(a.budgetNgn) > 0 && Boolean(a.targetBeneficiaries)
    && (a.climateScreening !== 'required' || Boolean(a.targetFacilityId))
  )) && isActivityBudgetValid;
  const hasMinimumActivities = mode !== 'create' || form.activities.length >= 1;

  const activityCategoryOptions = referenceOptions.activityCategoryOptions ?? WORKPLAN_ACTIVITY_CATEGORIES;
  const beneficiaryClassOptions = referenceOptions.beneficiaryClassOptions ?? WORKPLAN_BENEFICIARY_CLASSES;

  const isStartDateValid = Boolean(form.startDate) && (mode !== 'create' || form.startDate >= todayIso());
  const isRegistrationValid =
    Boolean(form.department) &&
    form.planTitle.trim().length >= 10 &&
    Boolean(form.planType) &&
    Boolean(form.planCategory) &&
    isStartDateValid &&
    Boolean(form.endDate) &&
    (!form.startDate || !form.endDate || form.endDate > form.startDate) &&
    Number(form.budgetNgn) > 0 &&
    Boolean(form.planOwnerUuid) &&
    Boolean(form.geographicScope) &&
    Boolean(form.status) &&
    (String(form.planCategory).toLowerCase() !== 'reform plan' || (Boolean(form.reformCategory) && form.supportingDepartments.length > 0));
  const isLinkedActivityValid = hasMinimumActivities && areActivitiesValid;
  const canGoNext = [isRegistrationValid, isLinkedActivityValid, true][step] ?? true;
  const recordStatusLabel = mode === 'create'
    ? 'Draft (system-set)'
    : `${String(row?.data_status ?? row?.dataStatus ?? 'draft').replace(/^\w/, (c) => c.toUpperCase())} (current)`;

  const set = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setSupportingDepartments = (values) => setForm((prev) => ({ ...prev, supportingDepartments: values }));

  const handleSubmit = async () => {
    if (form.planTitle.trim().length < 10) {
      setSubmitError('Plan title must be at least 10 characters (naming convention: [Institution] - [Type] - [Period] - [Focus]).');
      return;
    }
    if (!isStartDateValid) {
      setSubmitError('Start date is required and cannot be in the past for a new plan.');
      return;
    }
    if (form.startDate && form.endDate && form.endDate <= form.startDate) {
      setSubmitError('End date must be after start date.');
      return;
    }
    if (!(Number(form.budgetNgn) > 0)) {
      setSubmitError('Budget must be greater than 0.');
      return;
    }
    if (!hasMinimumActivities) {
      setSubmitError('At least one activity is required.');
      return;
    }
    if (!areActivitiesValid) {
      setSubmitError('Each activity needs a title (min 5 characters), category, budget, target beneficiaries, and a target facility when climate screening is required — and their combined budget cannot exceed the remaining workplan budget.');
      return;
    }

    setSubmitError('');
    setSubmitting(true);
    try {
      const result = await onSubmit({
        department: form.department,
        title: form.planTitle,
        planTitle: form.planTitle,
        planType: form.planType,
        planCategory: form.planCategory,
        startDate: form.startDate,
        endDate: form.endDate,
        budget: form.budgetNgn,
        budgetNgn: form.budgetNgn,
        planOwnerUuid: form.planOwnerUuid,
        geographicScope: form.geographicScope,
        status: form.status,
        completionPercent: form.completionPercent,
        reformCategory: form.planCategory === 'Reform Plan' ? form.reformCategory : '',
        supportingDepartments: form.planCategory === 'Reform Plan' ? form.supportingDepartments : [],
        activities: form.activities,
      });
      // The workplan itself saved fine, but one or more activity rows
      // failed to attach — stay on the form (don't close) so the warning
      // is visible, mirroring the Actor+Qualification partial-failure
      // pattern. Resubmitting will not duplicate the workplan since the
      // caller only creates activities on this path, not the workplan.
      if (result?.warning) {
        setSubmitError(result.warning);
        setSubmitting(false);
        return;
      }
      onClose();
    } catch (err) {
      setSubmitError(extractApiError(err, 'Failed to save workplan.'));
    } finally {
      setSubmitting(false);
    }
  };

  const departmentOptions = referenceOptions.departmentOptions ?? WORKPLAN_DEPARTMENTS;
  const planTypeOptions = referenceOptions.planTypeOptions ?? WORKPLAN_PLAN_TYPES;
  const planCategoryOptions = referenceOptions.planCategoryOptions ?? WORKPLAN_PLAN_CATEGORIES;
  const geoScopeOptions = referenceOptions.geoScopeOptions ?? WORKPLAN_GEO_SCOPES;
  const statusOptions = referenceOptions.workplanStatusOptions ?? WORKPLAN_STATUSES;

  if (mode === 'view') {
    const statusKey = String(form.status ?? '').toLowerCase();
    const dotCls = VIEW_STATUS_DOT[statusKey] ?? VIEW_STATUS_DOT.draft;
    const pillCls = VIEW_STATUS_PILL[statusKey] ?? VIEW_STATUS_PILL.draft;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
            <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">Status:</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${pillCls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
              {form.status || 'draft'}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <SectionHead title="Workplan Registration" />
          <ReviewRow label="Department" value={form.department} />
          <ReviewRow label="Plan Title" value={form.planTitle} />
          <ReviewRow label="Plan Type" value={form.planType} />
          <ReviewRow label="Plan Category" value={form.planCategory} />
          <ReviewRow label="Start Date" value={form.startDate} />
          <ReviewRow label="End Date" value={form.endDate} />
          <ReviewRow label="Budget (NGN)" value={form.budgetNgn} />
          <ReviewRow label="Plan Owner" value={resolveLabel(actorOptions, form.planOwnerUuid)} />
          <ReviewRow label="Geographic Scope" value={form.geographicScope} />
          <ReviewRow label="Status" value={form.status} />
          <ReviewRow
            label="Completion Percent"
            value={row?.completionPercent != null ? `${row.completionPercent}%` : 'Calculated once activities are linked and reported'}
          />
          <ActivitiesReviewList existingActivities={existingActivities} newActivities={form.activities} />
          {String(form.planCategory).toLowerCase() === 'reform plan' && (
            <>
              <SectionHead title="Reform" />
              <ReviewRow label="Reform Category" value={form.reformCategory} />
              <ReviewRow label="Supporting Departments" value={form.supportingDepartments?.join(', ')} />
            </>
          )}
        </div>

        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Back
          </button>
          {canEdit && (
            <button type="button" onClick={onEdit} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">
              Edit Record
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
    <RecordWizard
      steps={[
        { key: 'workplan', label: 'Workplan Registration' },
        { key: 'activity', label: 'Linked Activity' },
        { key: 'system', label: 'System Info' },
        { key: 'review', label: 'Review' },
      ]}
      currentStep={step}
      title={mode === 'create' ? 'Add New Record' : 'Edit Record'}
      subtitle={mode === 'create' ? 'Fill in the details to create a new record' : 'Update the details for this record'}
      statusLabel="draft"
      onCancel={onClose}
      onBack={() => setStep((current) => Math.max(0, current - 1))}
      onNext={() => setStep((current) => Math.min(REVIEW_STEP, current + 1))}
      canGoNext={canGoNext}
      nextLabel={step === REVIEW_STEP - 1 ? 'Review' : 'Next'}
      footer={isReviewStep ? makeReviewFooter({ onBack: () => setStep(REVIEW_STEP - 1), onSubmit: handleSubmit, submitting, submitLabel: mode === 'create' ? 'Create' : 'Save Changes' }) : undefined}
      error={submitError}
    >
      {step === 0 && modulePanel}
      {isReviewStep ? (
        <>
          <SectionHead title="Workplan Registration" />
          <ReviewRow label="Department" value={form.department} />
          <ReviewRow label="Plan Title" value={form.planTitle} />
          <ReviewRow label="Plan Type" value={form.planType} />
          <ReviewRow label="Plan Category" value={form.planCategory} />
          <ReviewRow label="Start Date" value={form.startDate} />
          <ReviewRow label="End Date" value={form.endDate} />
          <ReviewRow label="Budget (NGN)" value={form.budgetNgn} />
          <ReviewRow label="Plan Owner" value={resolveLabel(actorOptions, form.planOwnerUuid)} />
          <ReviewRow label="Geographic Scope" value={form.geographicScope} />
          <ReviewRow label="Status" value={form.status} />
          {String(form.planCategory).toLowerCase() === 'reform plan' && (
            <>
              <SectionHead title="Reform" />
              <ReviewRow label="Reform Category" value={form.reformCategory} />
              <ReviewRow label="Supporting Departments" value={form.supportingDepartments?.join(', ')} />
            </>
          )}
          <ActivitiesReviewList existingActivities={existingActivities} newActivities={form.activities} />
          <SectionHead title="System" />
          <ReviewRow
            label="Completion Percent"
            value={row?.completionPercent != null ? `${row.completionPercent}%` : 'Calculated once activities are linked and reported'}
          />
          <ReviewRow label="Record Status" value={recordStatusLabel} />
        </>
      ) : (
        <div className="space-y-5">
          {step === 0 && (
          <section className="space-y-3">
            <SectionHead title="Workplan Registration" />
            <div className="grid grid-cols-2 gap-4">
              <SelectField
                label="Department"
                value={form.department}
                onChange={set('department')}
                options={departmentOptions}
                placeholder="Select department"
                required
              />
              <TextField label="Plan Title" value={form.planTitle} onChange={set('planTitle')} placeholder="[Institution] - [Type] - [Period] - [Focus]" required />
              <ToggleField
                label="Plan Type"
                value={form.planType}
                onChange={set('planType')}
                options={planTypeOptions}
                required
              />
              <ChoiceButtonsField
                label="Plan Category"
                value={form.planCategory}
                onChange={set('planCategory')}
                options={planCategoryOptions}
                required
              />
              <TextField label="Start Date" type="date" value={form.startDate} onChange={set('startDate')} min={mode === 'create' ? todayIso() : undefined} required />
              <TextField label="End Date" type="date" value={form.endDate} onChange={set('endDate')} required />
              <TextField label="Budget (NGN)" type="number" value={form.budgetNgn} onChange={set('budgetNgn')} placeholder="0" required />
              <RegistryField
                label="Plan Owner"
                value={form.planOwnerUuid}
                onChange={set('planOwnerUuid')}
                options={actorOptions}
                placeholder="Select plan owner"
                fallbackPlaceholder="No actors in the registry yet"
                onPropose={() => setProposeActorOpen(true)}
                required
              />
              <SelectField
                label="Geographic Scope"
                value={form.geographicScope}
                onChange={set('geographicScope')}
                options={geoScopeOptions}
                placeholder="Select scope"
                required
              />
              <SelectField
                label="Status"
                value={form.status}
                onChange={set('status')}
                options={statusOptions}
                placeholder="Select status"
                required
              />
            </div>
          </section>
          )}

          {step === 1 && (
          <>
          <WorkplanActivitiesRepeatable
            entries={form.activities}
            onChange={(next) => setForm((prev) => ({ ...prev, activities: next }))}
            categoryOptions={activityCategoryOptions}
            beneficiaryClassOptions={beneficiaryClassOptions}
            remainingPool={remainingActivityPool}
          />

          {existingActivities.length > 0 && (
            <>
              <SectionDivider />
              <section className="space-y-2">
                <SectionHead title="Already Linked Activities" />
                {existingActivities.map((activity) => (
                  <ReviewRow
                    key={activity.uuid ?? activity.id}
                    label={activity.activityTitle ?? activity.title ?? 'Activity'}
                    value={`${activity.category ?? ''} — ₦${Number(activity.budgetNgn ?? activity.budget_ngn ?? 0).toLocaleString()}`}
                  />
                ))}
              </section>
            </>
          )}
          </>
          )}

          {step === 2 && (
            <section className="space-y-3">
              <SectionHead title="System" />
              <div className="grid grid-cols-2 gap-4">
                <FieldContainer label="Completion Percent">
                  <ReadonlyBox>
                    {row?.completionPercent != null ? `${row.completionPercent}%` : 'Calculated once activities are linked and reported'}
                  </ReadonlyBox>
                </FieldContainer>
                <FieldContainer label="Record Status">
                  <ReadonlyBox>{recordStatusLabel}</ReadonlyBox>
                </FieldContainer>
              </div>
            </section>
          )}

          {step === 0 && String(form.planCategory).toLowerCase() === 'reform plan' && (
            <>
              <SectionDivider />
              <section className="space-y-3">
                <SectionHead title="Reform" />
                <div className="grid grid-cols-2 gap-4">
                  <SelectField
                    label="Reform Category"
                    value={form.reformCategory}
                    onChange={set('reformCategory')}
                    options={REFORM_CATEGORY_OPTIONS}
                    placeholder="Select reform category"
                    span2
                  />
                  <MultiField
                    label="Supporting Departments"
                    values={form.supportingDepartments}
                    onChange={setSupportingDepartments}
                    options={departmentOptions}
                    span2
                    hint="Select one or more departments."
                  />
                </div>
              </section>
            </>
          )}
        </div>
      )}
    </RecordWizard>
    <ProposeActorModal
      isOpen={proposeActorOpen}
      onClose={() => setProposeActorOpen(false)}
      onCreated={(actor) => {
        const uuid = actor?.uuid ?? actor?.id ?? '';
        if (!uuid) return;
        setProposedActor({ value: uuid, label: actor?.fullName ?? actor?.full_name ?? 'New actor' });
        setForm((prev) => ({ ...prev, planOwnerUuid: uuid }));
      }}
    />
    </>
  );
}

// "+ Propose" for the Activity Report's Parent Activity — adds one activity
// row to an approved workplan (same fields and rules as the workplan form's
// Linked Activity section), like the other registry "+ Propose" pop-ups.
const APPROVED_WORKPLAN_STATUSES = ['approved', 'active', 'completed'];

function ProposeActivityModal({ isOpen, onClose, onCreated, workplans = [], activities = [], categoryOptions, beneficiaryClassOptions }) {
  const [facilityOptions] = useFacilityOptions();
  const [form, setForm] = useState(() => ({ workplanUuid: '', ...emptyActivityEntry() }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setForm({ workplanUuid: '', ...emptyActivityEntry() });
    setError('');
    setBusy(false);
  }, [isOpen]);

  const approvedWorkplans = useMemo(
    () => workplans.filter((w) => APPROVED_WORKPLAN_STATUSES.includes(String(w?.status ?? '').trim().toLowerCase())),
    [workplans]
  );

  if (!isOpen) return null;

  const workplanOptions = approvedWorkplans.map((w) => ({ value: w.uuid ?? w.id, label: w.title ?? w.planTitle ?? 'Workplan' }));
  const workplan = approvedWorkplans.find((w) => (w.uuid ?? w.id) === form.workplanUuid) ?? null;
  const committed = activities
    .filter((a) => (a.workplanUuid ?? a.workplan_uuid) === form.workplanUuid)
    .reduce((sum, a) => sum + (Number(a.budgetNgn ?? a.budget_ngn ?? 0) || 0), 0);
  const remaining = workplan ? (Number(workplan.budgetNgn ?? workplan.budget_ngn ?? workplan.budget ?? 0) || 0) - committed : null;
  const set = (key) => (event) => { const value = event?.target ? event.target.value : event; setForm((prev) => ({ ...prev, [key]: value })); };
  const budgetOk = Number(form.budgetNgn) > 0 && (remaining === null || Number(form.budgetNgn) <= remaining);
  const canCreate = Boolean(form.workplanUuid) && form.activityTitle.trim().length >= 5 && Boolean(form.category)
    && budgetOk && Boolean(form.targetBeneficiaries)
    && (form.climateScreening !== 'required' || Boolean(form.targetFacilityId));

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    setError('');
    try {
      const record = await workplanActivitiesApi.create({
        workplan_uuid: form.workplanUuid,
        title: form.activityTitle.trim(),
        activity_title: form.activityTitle.trim(),
        category: form.category,
        budget_ngn: form.budgetNgn,
        target_beneficiaries: form.targetBeneficiaries,
        beneficiary_class: form.beneficiaryClass,
        climate_screening: form.climateScreening,
        target_facility_id: form.targetFacilityId,
        status: 'Planned',
        activity_status: 'Planned',
      });
      const workplanTitle = workplan?.title ?? workplan?.planTitle ?? '';
      onCreated?.({
        ...record,
        workplanUuid: form.workplanUuid,
        workplan: workplanTitle,
        title: record?.title || form.activityTitle.trim(),
        targetBeneficiaries: record?.targetBeneficiaries ?? form.targetBeneficiaries,
        climateScreening: form.climateScreening,
        targetFacilityId: form.climateScreening === 'required' ? form.targetFacilityId : '',
      });
      onClose();
    } catch (err) {
      setError(extractApiError(err, 'Failed to propose the activity.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1250] flex items-center justify-center bg-black/50 px-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Propose New Activity</h3>
            <p className="mt-0.5 text-xs text-gray-400">Registry proposal — adds an activity to an approved workplan</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-full text-xl leading-none text-gray-400 hover:bg-gray-100">×</button>
        </div>
        <div className="space-y-4 p-6">
          {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-700">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label="Workplan"
              value={form.workplanUuid}
              onChange={set('workplanUuid')}
              options={workplanOptions}
              placeholder={workplanOptions.length ? 'Select approved workplan' : 'No approved workplans yet'}
              hint={remaining !== null ? `Remaining budget: ₦${Math.max(remaining, 0).toLocaleString()}` : 'Activities can only be added to an approved workplan.'}
              disabled={workplanOptions.length === 0}
              required
              span2
            />
            <TextField label="Activity Title" value={form.activityTitle} onChange={set('activityTitle')} placeholder="Activity title" hint="Minimum 5 characters" required span2 />
            <SelectField label="Category" value={form.category} onChange={set('category')} options={categoryOptions} placeholder="Select category" required />
            <TextField label="Budget (NGN)" type="number" value={form.budgetNgn} onChange={set('budgetNgn')} placeholder="0"
              hint={remaining !== null && Number(form.budgetNgn) > remaining ? 'Exceeds the remaining workplan budget.' : undefined} required />
            <SelectField label="Beneficiary Class" value={form.beneficiaryClass} onChange={set('beneficiaryClass')} options={beneficiaryClassOptions} placeholder="Select (optional)" />
            <TextField label="Target Beneficiaries" type="number" value={form.targetBeneficiaries} onChange={set('targetBeneficiaries')} placeholder="0" required />
            <ToggleField
              label="Climate Screening"
              value={form.climateScreening}
              onChange={(value) => setForm((prev) => ({ ...prev, climateScreening: value || 'not_applicable', targetFacilityId: value === 'required' ? prev.targetFacilityId : '' }))}
              options={CLIMATE_SCREENING_OPTIONS}
              required
              span2
            />
            {form.climateScreening === 'required' && (
              <SelectField label="Target Facility" value={form.targetFacilityId} onChange={set('targetFacilityId')} options={facilityOptions}
                placeholder="Select from registry" hint="The facility this intervention is assessed on." required span2 />
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="h-11 flex-1 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Close</button>
            <button type="button" onClick={submit} disabled={!canCreate || busy}
              className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40">
              {busy ? 'Proposing…' : 'Propose'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportForm({ onSubmit, onClose, context = {}, modulePanel = null, mode = 'create', row = null, onEdit, canEdit }) {
  const referenceOptions = context.referenceOptions ?? {};
  const [proposeOpen, setProposeOpen] = useState(null); // 'activity' | 'ppp' | 'assessment'
  const [proposedActivity, setProposedActivity] = useState(null);
  const [proposedPpp, setProposedPpp] = useState(null);
  const [proposeNotice, setProposeNotice] = useState('');
  const activityChoices = useMemo(() => {
    const base = referenceOptions.activityOptions ?? [];
    return proposedActivity ? [proposedActivity, ...base.filter((option) => option.value !== proposedActivity.value)] : base;
  }, [referenceOptions.activityOptions, proposedActivity]);
  const pppChoices = useMemo(() => {
    const base = referenceOptions.pppOptions ?? [];
    return proposedPpp ? [proposedPpp, ...base.filter((option) => option.value !== proposedPpp.value)] : base;
  }, [referenceOptions.pppOptions, proposedPpp]);

  const [form, setForm] = useState({
    activityType: 'Standard',
    workplanActivityUuid: '',
    pppId: '',
    reportingPeriodType: '',
    reportingPeriod: '',
    actualStartDate: '',
    executionStatus: '',
    actualExpenditureNgn: '',
    outputsTarget: '',
    outputsAchieved: '',
    submissionDate: '',
    varianceExplanation: '',
    attachments: '',
    attachmentsFileName: '',
    facilityAssessmentRef: '',
  });
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const isReviewStep = step === 1;
  // Press's request was specifically for video/link attachments alongside
  // files — a URL is stored in the same `attachments`/`attachmentsFileName`
  // pair a file upload would use, just as plain text instead of a data URI.
  const [attachmentMode, setAttachmentMode] = useState('file');

  useEffect(() => {
    if (!row) return;
    const reportingPeriod = row.reportingPeriod ?? row.reporting_period ?? '';
    setForm({
      activityType: row.activityType ?? row.activity_type ?? 'Standard',
      workplanActivityUuid: row.activityUuid ?? row.workplan_activity_uuid ?? row.workplanActivityUuid ?? '',
      pppId: row.pppId ?? row.ppp_id ?? '',
      reportingPeriodType: guessReportPeriodType(reportingPeriod),
      reportingPeriod,
      actualStartDate: formatDateInput(row.actualStartDate ?? row.actual_start_date ?? ''),
      executionStatus: row.executionStatus ?? row.execution_status ?? '',
      actualExpenditureNgn: String(row.actualExpenditureNgn ?? row.actual_expenditure_ngn ?? row.expenditureNgn ?? row.expenditure_ngn ?? ''),
      outputsTarget: row.outputsTarget ?? row.outputs_target ?? '',
      outputsAchieved: row.outputsAchieved ?? row.outputs_achieved ?? '',
      submissionDate: row.submissionDate ?? row.submission_date ?? '',
      varianceExplanation: row.varianceExpl ?? row.variance_explanation ?? row.varianceExplanation ?? '',
      attachments: row.attachments ?? '',
      attachmentsFileName: row.attachmentsFileName ?? row.attachments_file_name ?? '',
      facilityAssessmentRef: row.facilityAssessmentRef ?? row.facility_assessment_ref ?? '',
    });
    const existingAttachment = row.attachments ?? '';
    setAttachmentMode(existingAttachment && !existingAttachment.startsWith('data:') ? 'link' : 'file');
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPppMilestone = String(form.activityType).toLowerCase() === 'ppp milestone';
  // v2.2 climate: when the parent activity needs climate screening and the
  // report is Completed, it must link to the approved emissions-screening
  // assessment recorded on the activity's target facility.
  const parentActivity = isPppMilestone ? null
    : activityChoices.find((option) => option.value === form.workplanActivityUuid)?.raw ?? null;
  const parentTargetFacilityId = parentActivity?.targetFacilityId ?? parentActivity?.target_facility_id ?? '';
  const screeningRequired = (parentActivity?.climateScreening ?? parentActivity?.climate_screening) === 'required';
  const needsAssessmentRef = screeningRequired && String(form.executionStatus).toLowerCase() === 'completed';
  const assessmentRefOptions = useMemo(() => (screeningRequired
    ? approvedClimateAssessments({ facilityUuid: parentTargetFacilityId || undefined, assessmentType: 'emissions_screening' })
      .map((row) => ({
        value: row.uuid,
        label: [row.facility_name, row.assessment_date, labelOf(EMISSIONS_CLASSIFICATION_OPTIONS, row.emissions_classification)].filter(Boolean).join(' — '),
      }))
    : []), [screeningRequired, parentTargetFacilityId]);
  const isVarianceRequired = Boolean(form.executionStatus) && String(form.executionStatus).toLowerCase() !== 'completed';
  const isExpenditureValid = form.actualExpenditureNgn !== '' && Number(form.actualExpenditureNgn) >= 0;
  const isStartDateValid = Boolean(form.actualStartDate) && form.actualStartDate <= todayIso();
  const canGoNext =
    Boolean(form.activityType) &&
    (isPppMilestone ? Boolean(form.pppId.trim()) : Boolean(form.workplanActivityUuid)) &&
    Boolean(form.reportingPeriodType) &&
    Boolean(form.reportingPeriod) &&
    isStartDateValid &&
    Boolean(form.executionStatus) &&
    isExpenditureValid &&
    Boolean(form.outputsAchieved.trim()) &&
    (!isVarianceRequired || form.varianceExplanation.trim().length >= 20) &&
    (!needsAssessmentRef || Boolean(form.facilityAssessmentRef));

  const set = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setReportingPeriodType = (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, reportingPeriodType: value, reportingPeriod: '' }));
  };

  // outputs_target is auto-derived from the parent activity's target
  // beneficiaries — real client-side computation, not a decorative
  // placeholder, so the reviewer can see the actual figure before saving.
  useEffect(() => {
    if (mode !== 'create' || isPppMilestone || !form.workplanActivityUuid) return;
    const matched = activityChoices.find((option) => option.value === form.workplanActivityUuid);
    const target = matched?.raw?.targetBeneficiaries ?? matched?.raw?.target_beneficiaries ?? '';
    setForm((prev) => (prev.outputsTarget === String(target ?? '') ? prev : { ...prev, outputsTarget: target ?? '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.workplanActivityUuid, isPppMilestone, mode, activityChoices]);

  const handleSubmit = async () => {
    if (!isPppMilestone && !form.workplanActivityUuid.trim()) {
      setSubmitError('Workplan activity is required.');
      return;
    }
    if (isPppMilestone && !form.pppId.trim()) {
      setSubmitError('PPP ID is required for PPP milestone reports.');
      return;
    }
    if (!form.reportingPeriodType || !form.reportingPeriod) {
      setSubmitError('Reporting period is required.');
      return;
    }
    if (!isStartDateValid) {
      setSubmitError('Actual start date is required and cannot be in the future.');
      return;
    }
    if (!isExpenditureValid) {
      setSubmitError('Actual expenditure (NGN) is required and cannot be negative.');
      return;
    }
    if (isVarianceRequired && form.varianceExplanation.trim().length < 20) {
      setSubmitError('Variance explanation is required (min 20 characters) when execution status is not Completed.');
      return;
    }
    if (needsAssessmentRef && !form.facilityAssessmentRef) {
      setSubmitError('Facility climate assessment is required — the parent activity needs climate screening and this report is Completed.');
      return;
    }

    setSubmitError('');
    setSubmitting(true);
    try {
      await onSubmit({
        activityType: form.activityType,
        workplanActivityUuid: isPppMilestone ? '' : form.workplanActivityUuid,
        parentActivityId: isPppMilestone ? '' : form.workplanActivityUuid,
        pppId: isPppMilestone ? form.pppId : '',
        reportingPeriod: form.reportingPeriod,
        actualStartDate: form.actualStartDate,
        executionStatus: form.executionStatus,
        actualExpenditureNgn: form.actualExpenditureNgn,
        outputsTarget: form.outputsTarget,
        outputsAchieved: form.outputsAchieved,
        submissionDate: form.submissionDate,
        varianceExplanation: form.varianceExplanation,
        attachments: form.attachments,
        attachmentsFileName: form.attachmentsFileName,
        facilityAssessmentRef: screeningRequired ? form.facilityAssessmentRef : '',
      });
      onClose();
    } catch (err) {
      setSubmitError(extractApiError(err, 'Failed to save report.'));
    } finally {
      setSubmitting(false);
    }
  };

  const reportTypeOptions = referenceOptions.reportTypeOptions ?? WORKPLAN_REPORT_TYPES;
  const reportingPeriodInstanceOptions = reportPeriodInstances(form.reportingPeriodType);
  const executionStatusOptions = referenceOptions.reportStatusOptions ?? ACTIVITY_REPORT_STATUSES;

  if (mode === 'view') {
    const statusKey = String(form.executionStatus ?? '').toLowerCase();
    const dotCls = VIEW_STATUS_DOT[statusKey] ?? VIEW_STATUS_DOT.draft;
    const pillCls = VIEW_STATUS_PILL[statusKey] ?? VIEW_STATUS_PILL.draft;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
            <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">Status:</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${pillCls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
              {form.executionStatus || 'draft'}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <SectionHead title="Activity Reports" />
          <ReviewRow label="Activity Type" value={form.activityType} />
          <ReviewRow
            label={isPppMilestone ? 'PPP ID' : 'Parent Activity'}
            value={isPppMilestone
              ? resolveLabel(pppChoices, form.pppId)
              : resolveLabel(activityChoices, form.workplanActivityUuid)}
          />
          <ReviewRow label="Period Type" value={form.reportingPeriodType} />
          <ReviewRow label="Reporting Period" value={form.reportingPeriod} />
          <ReviewRow label="Actual Start Date" value={form.actualStartDate} />
          <ReviewRow label="Execution Status" value={form.executionStatus} />
          <ReviewRow label="Actual Expenditure (NGN)" value={form.actualExpenditureNgn} />
          <ReviewRow label="Outputs Target" value={form.outputsTarget || 'Calculated by the system from the linked activity'} />
          <ReviewRow label="Outputs Achieved" value={form.outputsAchieved} />
          <ReviewRow label="Variance Explanation" value={form.varianceExplanation} />
          <ReviewRow label="Attachments" value={form.attachments ? (form.attachmentsFileName || 'File attached') : ''} />
          {(needsAssessmentRef || form.facilityAssessmentRef) && (
            <ReviewRow label="Facility Climate Assessment" value={resolveLabel(assessmentRefOptions, form.facilityAssessmentRef) || (form.facilityAssessmentRef ? 'Linked assessment' : '')} />
          )}
          <ReviewRow label="Submission Date" value={form.submissionDate || 'auto system-set'} />
        </div>

        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Back
          </button>
          {canEdit && (
            <button type="button" onClick={onEdit} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">
              Edit Record
            </button>
          )}
        </div>
      </div>
    );
  }

  const proposeModals = (
    <>
      <ProposeActivityModal
        isOpen={proposeOpen === 'activity'}
        onClose={() => setProposeOpen(null)}
        workplans={context.workplans ?? []}
        activities={context.activities ?? []}
        categoryOptions={referenceOptions.activityCategoryOptions ?? WORKPLAN_ACTIVITY_CATEGORIES}
        beneficiaryClassOptions={referenceOptions.beneficiaryClassOptions ?? WORKPLAN_BENEFICIARY_CLASSES}
        onCreated={(activity) => {
          const uuid = activity?.uuid ?? activity?.id;
          if (!uuid) return;
          setProposedActivity({ value: uuid, label: activity.workplan ? `${activity.title} (${activity.workplan})` : activity.title, raw: activity });
          setForm((prev) => ({ ...prev, workplanActivityUuid: uuid }));
        }}
      />
      {proposeOpen === 'ppp' && (
        <CreateNewModal
          tab="ppp"
          onClose={() => setProposeOpen(null)}
          onSubmit={async (payload) => {
            const record = await pppArrangementsApi.create(payload);
            const uuid = record?.uuid ?? record?.id;
            if (!uuid) return;
            setProposedPpp({ value: uuid, label: record.pppArrangementId || record.partnerOrgName || 'New PPP arrangement' });
            setForm((prev) => ({ ...prev, pppId: uuid }));
          }}
        />
      )}
      <ProposeClimateAssessmentModal
        isOpen={proposeOpen === 'assessment'}
        onClose={() => setProposeOpen(null)}
        facilityUuid={parentTargetFacilityId}
        activityUuid={form.workplanActivityUuid}
        activityTitle={activityChoices.find((option) => option.value === form.workplanActivityUuid)?.label ?? ''}
        onCreated={() => setProposeNotice('Climate assessment proposed as a Draft. It can be linked here once it has been approved.')}
      />
    </>
  );

  return (
    <>
    {proposeModals}
    <RecordWizard
      steps={[
        { key: 'report', label: 'Activity Report' },
        { key: 'review', label: 'Review' },
      ]}
      currentStep={step}
      title={mode === 'create' ? 'Add New Record' : 'Edit Record'}
      subtitle={mode === 'create' ? 'Fill in the details to create a new record' : 'Update the details for this record'}
      statusLabel="draft"
      onCancel={onClose}
      onBack={() => setStep((current) => Math.max(0, current - 1))}
      onNext={() => setStep(1)}
      canGoNext={canGoNext}
      nextLabel="Review"
      footer={isReviewStep ? makeReviewFooter({ onBack: () => setStep(0), onSubmit: handleSubmit, submitting, submitLabel: mode === 'create' ? 'Create' : 'Save Changes' }) : undefined}
      error={submitError}
    >
      {!isReviewStep && modulePanel}
      {isReviewStep ? (
        <>
          <SectionHead title="Activity Reports" />
          <ReviewRow label="Activity Type" value={form.activityType} />
          <ReviewRow
            label={isPppMilestone ? 'PPP ID' : 'Parent Activity'}
            value={isPppMilestone
              ? resolveLabel(pppChoices, form.pppId)
              : resolveLabel(activityChoices, form.workplanActivityUuid)}
          />
          <ReviewRow label="Period Type" value={form.reportingPeriodType} />
          <ReviewRow label="Reporting Period" value={form.reportingPeriod} />
          <ReviewRow label="Actual Start Date" value={form.actualStartDate} />
          <ReviewRow label="Execution Status" value={form.executionStatus} />
          <ReviewRow label="Actual Expenditure (NGN)" value={form.actualExpenditureNgn} />
          <ReviewRow label="Outputs Target" value={form.outputsTarget || 'Calculated by the system from the linked activity'} />
          <ReviewRow label="Outputs Achieved" value={form.outputsAchieved} />
          <ReviewRow label="Variance Explanation" value={form.varianceExplanation} />
          <ReviewRow label="Attachments" value={form.attachments ? (form.attachmentsFileName || 'File attached') : ''} />
          {(needsAssessmentRef || form.facilityAssessmentRef) && (
            <ReviewRow label="Facility Climate Assessment" value={resolveLabel(assessmentRefOptions, form.facilityAssessmentRef) || (form.facilityAssessmentRef ? 'Linked assessment' : '')} />
          )}
          <ReviewRow label="Submission Date" value={form.submissionDate || 'auto system-set'} />
        </>
      ) : (
        <div className="space-y-5">

          <section className="space-y-3">
            <SectionHead title="Activity Reports" />
            <div className="grid grid-cols-2 gap-4">
              <SelectField
                label="Activity Type"
                value={form.activityType}
                onChange={set('activityType')}
                options={reportTypeOptions}
                placeholder="Select activity type"
                required
              />
              <RegistryField
                label="Parent Activity"
                value={isPppMilestone ? '' : form.workplanActivityUuid}
                onChange={set('workplanActivityUuid')}
                options={activityChoices}
                placeholder="Select from registry"
                fallbackPlaceholder="No approved activities yet"
                hint={isPppMilestone
                  ? 'Not used for PPP Milestone reports — the PPP ID links the report instead.'
                  : 'Required unless Activity Type is PPP Milestone. Only activities under an approved workplan are listed.'}
                onPropose={() => setProposeOpen('activity')}
                disabled={isPppMilestone}
                required={!isPppMilestone}
              />
              <RegistryField
                label="PPP ID"
                value={isPppMilestone ? form.pppId : ''}
                onChange={set('pppId')}
                options={pppChoices}
                placeholder="Select from registry"
                fallbackPlaceholder="No PPP arrangements yet"
                hint={isPppMilestone
                  ? 'Required — the PPP Arrangement this milestone reports on.'
                  : 'Only used when Activity Type is PPP Milestone.'}
                onPropose={() => setProposeOpen('ppp')}
                disabled={!isPppMilestone}
                required={isPppMilestone}
              />
              <SelectField
                label="Period Type"
                value={form.reportingPeriodType}
                onChange={setReportingPeriodType}
                options={REPORT_PERIOD_TYPE_OPTIONS}
                placeholder="Select period type"
                required
              />
              <SelectField
                label="Period Instance"
                value={form.reportingPeriod}
                onChange={set('reportingPeriod')}
                options={reportingPeriodInstanceOptions}
                placeholder={form.reportingPeriodType ? 'Select period' : 'Select type first'}
                required
              />
              <TextField label="Actual Start Date" type="date" value={form.actualStartDate} onChange={set('actualStartDate')} max={todayIso()} required />
              <SelectField
                label="Execution Status"
                value={form.executionStatus}
                onChange={set('executionStatus')}
                options={executionStatusOptions}
                placeholder="Select status"
                required
              />
              <TextField label="Actual Expenditure (NGN)" type="number" value={form.actualExpenditureNgn} onChange={set('actualExpenditureNgn')} placeholder="0" required />
              <div className="col-span-2">
                <FieldContainer label="Outputs Target">
                  <ReadonlyBox>
                    {form.outputsTarget || 'Calculated by the system from the linked activity'}
                  </ReadonlyBox>
                </FieldContainer>
              </div>
              <div className="col-span-2">
                <TextAreaField
                  label="Outputs Achieved"
                  value={form.outputsAchieved}
                  onChange={set('outputsAchieved')}
                  placeholder="Structured summary, e.g. farmers_trained: 120"
                  rows={3}
                  required
                />
              </div>
              <div className="col-span-2">
                <TextAreaField
                  label="Variance Explanation"
                  value={form.varianceExplanation}
                  onChange={set('varianceExplanation')}
                  placeholder="Required when the status is not completed"
                  rows={4}
                  required={isVarianceRequired}
                  hint={isVarianceRequired
                    ? 'Required — minimum 20 characters — since execution status is not Completed.'
                    : 'Provide a clear explanation when the activity is partially completed, delayed, or not started.'}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <div className="flex gap-4 text-xs font-medium text-gray-500">
                  <label className="flex cursor-pointer items-center gap-1.5">
                    <input type="radio" checked={attachmentMode === 'file'} onChange={() => { setAttachmentMode('file'); setForm((p) => ({ ...p, attachments: '', attachmentsFileName: '' })); }} className="accent-green-600" />
                    Upload file
                  </label>
                  <label className="flex cursor-pointer items-center gap-1.5">
                    <input type="radio" checked={attachmentMode === 'link'} onChange={() => { setAttachmentMode('link'); setForm((p) => ({ ...p, attachments: '', attachmentsFileName: '' })); }} className="accent-green-600" />
                    Paste a link
                  </label>
                </div>
                {attachmentMode === 'file' ? (
                  <FileUploadField
                    label="Attachments"
                    value={form.attachments}
                    fileName={form.attachmentsFileName}
                    onChange={(dataUri, name) => setForm((p) => ({ ...p, attachments: dataUri, attachmentsFileName: name }))}
                    accept="image/*,video/*,.pdf"
                    maxSizeMB={10}
                    hint="Image, video, or PDF — max 10MB"
                  />
                ) : (
                  <TextField
                    label="Attachments"
                    type="url"
                    value={form.attachments}
                    onChange={(e) => setForm((p) => ({ ...p, attachments: e.target.value, attachmentsFileName: e.target.value }))}
                    placeholder="https://... (e.g. a video or article link)"
                    hint="Link to engagement media or evidence hosted elsewhere (e.g. a video or press link)."
                  />
                )}
              </div>
              <div className="col-span-2 space-y-3 rounded-2xl border border-dashed border-green-300 bg-green-50/40 p-4">
                <SectionHead title="Climate" />
                <RegistryField
                  label="Facility Climate Assessment"
                  value={screeningRequired ? form.facilityAssessmentRef : ''}
                  onChange={set('facilityAssessmentRef')}
                  options={assessmentRefOptions}
                  placeholder="Select from registry"
                  fallbackPlaceholder={screeningRequired ? 'No approved emissions screening for this facility yet' : 'Not required for this activity'}
                  hint={!screeningRequired
                    ? 'Only used when the parent activity has Climate Screening = Required.'
                    : needsAssessmentRef
                      ? 'Required — link the approved emissions-screening assessment recorded on the activity’s target facility.'
                      : 'Becomes mandatory when Execution Status is Completed. Links the approved emissions-screening assessment on the activity’s target facility.'}
                  onPropose={() => setProposeOpen('assessment')}
                  disabled={!screeningRequired}
                  required={needsAssessmentRef}
                  span2
                />
                {proposeNotice && <p className="text-xs font-medium text-green-700">{proposeNotice}</p>}
              </div>
              <div className="col-span-2">
                <FieldContainer label="Submission Date">
                  <ReadonlyBox>auto system-set</ReadonlyBox>
                </FieldContainer>
              </div>
            </div>
          </section>
        </div>
      )}
    </RecordWizard>
    </>
  );
}

const COOP_TYPE_OPTIONS = Object.entries(COOP_TYPE_MAP).map(([value, label]) => ({ value, label }));

// DCIC-10 Organisation / Cooperative — registry-first "propose if absent"
// escape hatch for REG-PPP's partner_org_id (spec: "registry-first; FK
// resolves (propose via DCIC-16 if absent)"). Mirrors ProposeBreedModal's
// quick-add pattern elsewhere in this app.
function ProposeOrganisationModal({ isOpen, onClose, onCreated }) {
  const { user } = useAuth();
  const BLANK = {
    organisationName: '', organisationType: '', regNumberOrg: '',
    state: '', lga: '', totalMembers: '', activeMembers: '', primaryCommodity: '',
  };
  const [form, setForm] = useState(BLANK);
  const { stateOptions, lgaOptions, loadingLgas } = useLocationOptions(form.state, form.lga);
  const { lockedState: orgLockedState, lockedLga: orgLockedLga } = useApplyScopeLock(form, setForm, { enabled: isOpen });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (isOpen) {
      setForm(BLANK);
      setSaving(false); setSuccess(false); setErr('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const set = (key) => (e) => setForm((p) => ({
    ...p,
    [key]: e.target.value,
    ...(key === 'state' ? { lga: '' } : {}),
  }));

  const isValid = form.organisationName.trim().length >= 3
    && Boolean(form.organisationType)
    && form.regNumberOrg.trim().length >= 5
    && Boolean(form.state)
    && Number(form.totalMembers) >= 1
    && form.activeMembers !== '' && Number(form.activeMembers) <= Number(form.totalMembers)
    && form.primaryCommodity.trim();

  const handleSubmit = async () => {
    if (!isValid || saving) return;
    setSaving(true); setErr('');
    try {
      const created = await cooperativesApi.create({
        organisation_name: form.organisationName.trim(),
        organisation_type: form.organisationType,
        reg_number_org: form.regNumberOrg.trim(),
        state: form.state,
        lga: form.lga,
        total_members: Number(form.totalMembers),
        active_members: Number(form.activeMembers),
        primary_commodity: form.primaryCommodity.trim(),
      });
      setSuccess(true);
      logRegistryProposal('cooperative', { name: form.organisationName, state: form.state, lga: form.lga }, user);
      onCreated?.(created);
    } catch (e) {
      setErr(e?.response?.data?.message ?? e.message ?? 'Submission failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Propose New Organisation</h3>
            <p className="text-xs text-gray-400 mt-0.5">Registry proposal — pending admin review</p>
          </div>
          <button type="button" onClick={onClose} className="h-7 w-7 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {err && <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-2.5 text-xs text-red-700">{err}</div>}

          {success ? (
            <div className="rounded-lg bg-green-50 border border-green-100 px-4 py-4 text-center space-y-3">
              <p className="text-sm font-semibold text-green-800">Proposal submitted successfully.</p>
              <p className="text-xs text-green-700">This organisation has been added to the registry and selected as the partner.</p>
              <button type="button" onClick={onClose} className="mt-2 px-5 py-2 rounded-full bg-green-600 text-xs font-semibold text-white hover:bg-green-700">Close</button>
            </div>
          ) : (
            <>
              <TextField label="Organisation Name" required value={form.organisationName} onChange={set('organisationName')} placeholder="e.g. Sahel AgriVentures Ltd" />
              <SelectField label="Organisation Type" required value={form.organisationType} onChange={set('organisationType')} options={COOP_TYPE_OPTIONS} placeholder="Select type" />
              <TextField label="Registration Number" required value={form.regNumberOrg} onChange={set('regNumberOrg')} placeholder="CAC or other, min 5 chars" />
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="State" required value={form.state} onChange={set('state')} options={stateOptions} placeholder="Select state" disabled={Boolean(orgLockedState)} />
                <SelectField label="LGA" value={form.lga} onChange={set('lga')} options={lgaOptions} placeholder={form.state ? (loadingLgas ? 'Loading…' : 'Select LGA') : 'Select state first'} disabled={Boolean(orgLockedLga) || !form.state || loadingLgas} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Total Members" required type="number" value={form.totalMembers} onChange={set('totalMembers')} placeholder="0" />
                <TextField label="Active Members" required type="number" value={form.activeMembers} onChange={set('activeMembers')} placeholder="0" />
              </div>
              <TextField label="Primary Commodity" required value={form.primaryCommodity} onChange={set('primaryCommodity')} placeholder="e.g. Cattle, Poultry" />

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={onClose} className="flex-1 h-10 rounded-full border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="button" onClick={handleSubmit} disabled={!isValid || saving} className="flex-1 h-10 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  {saving ? 'Submitting…' : 'Submit Proposal'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// REG-PPP — PPP Arrangement Registry. Cross-cutting Class A registry (v2.0):
// partner = DCIC-10 organisation (registry-first); facility = DCIC-06,
// conditional on ppp_type = infrastructure; progress/milestones are reported
// via DCIC-12 Activity Report (activity_type = ppp_milestone), not here.
function PppForm({ onSubmit, onClose, modulePanel = null, mode = 'create', row = null, onEdit, canEdit }) {
  const [form, setForm] = useState({
    partnerOrgUuid: '',
    consortiumPartnerOrgUuids: [],
    pppType: '',
    arrangementModel: '',
    facilityUuid: '',
    investmentValueNgn: '',
    fmldContributionNgn: '',
    privateContributionNgn: '',
    commencementDate: '',
    durationMonths: '',
    expiryDate: '',
    state: '',
    lga: '',
    status: 'proposed',
    agreementDocument: '',
    agreementDocumentFileName: '',
  });
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const [proposeOrgOpen, setProposeOrgOpen] = useState(false);
  const isReviewStep = step === 1;
  const { stateOptions, lgaOptions, loadingLgas } = useLocationOptions(form.state, form.lga);
  const { lockedState: pppLockedState, lockedLga: pppLockedLga } = useApplyScopeLock(form, setForm, { enabled: mode !== 'view' });

  const [organisationOptions, setOrganisationOptions] = useState([]);
  const [facilityOptions, setFacilityOptions] = useState([]);
  useEffect(() => {
    cooperativesApi.listAll({ per_page: 500 })
      .then(({ items }) => setOrganisationOptions(
        (items ?? []).filter((o) => o.uuid).map((o) => ({ value: o.uuid, label: o.organisationName || o.organisation_name || 'Organisation' }))
      ))
      .catch(() => setOrganisationOptions([]));
    facilitiesApi.listAll({ per_page: 500 })
      .then(({ items }) => setFacilityOptions(
        (items ?? []).filter((f) => f.uuid).map((f) => ({ value: f.uuid, label: f.name || 'Facility' }))
      ))
      .catch(() => setFacilityOptions([]));
  }, []);

  useEffect(() => {
    if (!row) return;
    setForm({
      partnerOrgUuid: row.partnerOrgId ?? row.partner_org_id ?? '',
      consortiumPartnerOrgUuids: Array.isArray(row.consortiumPartnerOrgIds) ? row.consortiumPartnerOrgIds : [],
      pppType: row.pppType ?? row.ppp_type ?? '',
      arrangementModel: row.arrangementModel ?? row.arrangement_model ?? '',
      facilityUuid: row.facilityId ?? row.facility_id ?? '',
      investmentValueNgn: String(row.investmentValueNgn ?? row.investment_value_ngn ?? ''),
      fmldContributionNgn: String(row.fmldContributionNgn ?? row.fmld_contribution_ngn ?? ''),
      privateContributionNgn: String(row.privateContributionNgn ?? row.private_contribution_ngn ?? ''),
      commencementDate: formatDateInput(row.commencementDate ?? row.commencement_date ?? ''),
      durationMonths: String(row.durationMonths ?? row.duration_months ?? ''),
      expiryDate: formatDateInput(row.expiryDate ?? row.expiry_date ?? ''),
      state: row.state ?? '',
      lga: row.lga ?? '',
      status: row.status ?? 'proposed',
      agreementDocument: row.agreementDocument ?? row.agreement_document ?? '',
      agreementDocumentFileName: row.agreementDocumentFileName ?? row.agreement_document_file_name ?? '',
    });
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  const isInfrastructure = form.pppType === 'infrastructure';
  const investmentValue = Number(form.investmentValueNgn);
  const fmldContribution = form.fmldContributionNgn === '' ? null : Number(form.fmldContributionNgn);
  const privateContribution = form.privateContributionNgn === '' ? null : Number(form.privateContributionNgn);
  const contributionsValid =
    (fmldContribution === null || (Number.isFinite(fmldContribution) && fmldContribution >= 0 && fmldContribution <= investmentValue)) &&
    (privateContribution === null || (Number.isFinite(privateContribution) && privateContribution >= 0 && privateContribution <= investmentValue));
  const expiryAfterCommencement = !form.commencementDate || !form.expiryDate || form.expiryDate > form.commencementDate;

  const canGoNext =
    Boolean(form.partnerOrgUuid) &&
    Boolean(form.pppType) &&
    Boolean(form.arrangementModel) &&
    (!isInfrastructure || Boolean(form.facilityUuid)) &&
    Number.isFinite(investmentValue) && investmentValue >= 0 &&
    Boolean(form.commencementDate) &&
    Number(form.durationMonths) > 0 &&
    Boolean(form.expiryDate) &&
    expiryAfterCommencement &&
    Boolean(form.state) &&
    Boolean(form.status) &&
    contributionsValid;

  const set = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'state' ? { lga: '' } : {}),
      ...(key === 'pppType' && value !== 'infrastructure' ? { facilityUuid: '' } : {}),
    }));
  };

  const handleSubmit = async () => {
    if (isInfrastructure && !form.facilityUuid) {
      setSubmitError('Facility is required when PPP Type is Infrastructure.');
      return;
    }
    if (!expiryAfterCommencement) {
      setSubmitError('Expiry Date must be after the Commencement Date.');
      return;
    }
    if (!contributionsValid) {
      setSubmitError('FMLD and Private contributions must each be ≥0 and not exceed the total Investment Value.');
      return;
    }

    setSubmitError('');
    setSubmitting(true);
    try {
      await onSubmit({
        partnerOrgId: form.partnerOrgUuid,
        consortiumPartnerOrgIds: form.consortiumPartnerOrgUuids,
        pppType: form.pppType,
        arrangementModel: form.arrangementModel,
        facilityId: isInfrastructure ? form.facilityUuid : '',
        investmentValueNgn: form.investmentValueNgn,
        fmldContributionNgn: form.fmldContributionNgn,
        privateContributionNgn: form.privateContributionNgn,
        commencementDate: form.commencementDate,
        durationMonths: form.durationMonths,
        expiryDate: form.expiryDate,
        state: form.state,
        lga: form.lga,
        status: form.status,
        agreementDocument: form.agreementDocument,
        agreementDocumentFileName: form.agreementDocumentFileName,
      });
      onClose();
    } catch (err) {
      setSubmitError(extractApiError(err, 'Failed to save PPP arrangement.'));
    } finally {
      setSubmitting(false);
    }
  };

  const partnerLabel = organisationOptions.find((o) => o.value === form.partnerOrgUuid)?.label || form.partnerOrgUuid;
  const facilityLabel = facilityOptions.find((o) => o.value === form.facilityUuid)?.label || form.facilityUuid;

  const reviewRows = (
    <>
      <SectionHead title="PPP Arrangement" />
      <ReviewRow label="PPP Arrangement ID" value={row?.pppArrangementId || 'Assigned automatically on save'} />
      <ReviewRow label="Partner Organisation" value={partnerLabel} />
      {form.consortiumPartnerOrgUuids.length > 0 && (
        <ReviewRow
          label="Consortium Partners"
          value={form.consortiumPartnerOrgUuids.map((uuid) => resolveLabel(organisationOptions, uuid)).join(', ')}
        />
      )}
      <ReviewRow label="PPP Type" value={form.pppType} />
      <ReviewRow label="Arrangement Model" value={form.arrangementModel} />
      {isInfrastructure && <ReviewRow label="Facility" value={facilityLabel} />}
      <ReviewRow label="Investment Value (NGN)" value={form.investmentValueNgn} />
      <ReviewRow label="FMLD Contribution (NGN)" value={form.fmldContributionNgn} />
      <ReviewRow label="Private Contribution (NGN)" value={form.privateContributionNgn} />
      <ReviewRow label="Commencement Date" value={form.commencementDate} />
      <ReviewRow label="Duration (months)" value={form.durationMonths} />
      <ReviewRow label="Expiry Date" value={form.expiryDate} />
      <ReviewRow label="Geographic Scope" value={[form.state, form.lga].filter(Boolean).join(' / ')} />
      <ReviewRow label="Status" value={form.status} />
      <ReviewRow label="Agreement Document" value={form.agreementDocument ? (form.agreementDocumentFileName || 'File attached') : ''} />
    </>
  );

  if (mode === 'view') {
    const statusKey = String(form.status ?? '').toLowerCase();
    const dotCls = VIEW_STATUS_DOT[statusKey] ?? VIEW_STATUS_DOT.draft;
    const pillCls = VIEW_STATUS_PILL[statusKey] ?? VIEW_STATUS_PILL.draft;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
            <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">Status:</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${pillCls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
              {form.status || 'proposed'}
            </span>
          </div>
        </div>
        <div className="space-y-4">{reviewRows}</div>
        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Back
          </button>
          {canEdit && (
            <button type="button" onClick={onEdit} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">
              Edit Record
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
    <RecordWizard
      steps={[
        { key: 'ppp', label: 'PPP Arrangement' },
        { key: 'review', label: 'Review' },
      ]}
      currentStep={step}
      title={mode === 'create' ? 'Add New Record' : 'Edit Record'}
      subtitle={mode === 'create' ? 'Fill in the details to create a new record' : 'Update the details for this record'}
      statusLabel="draft"
      onCancel={onClose}
      onBack={() => setStep((current) => Math.max(0, current - 1))}
      onNext={() => setStep(1)}
      canGoNext={canGoNext}
      nextLabel="Review"
      footer={isReviewStep ? makeReviewFooter({ onBack: () => setStep(0), onSubmit: handleSubmit, submitting, submitLabel: mode === 'create' ? 'Save Arrangement' : 'Save Changes' }) : undefined}
      error={submitError}
    >
      {!isReviewStep && modulePanel}
      {isReviewStep ? reviewRows : (
        <div className="space-y-5">

          <section className="space-y-3">
            <SectionHead title="Partner & Classification" />
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <FieldContainer label="PPP Arrangement ID" hint="System-generated (PPP-[YEAR]-[SEQ]).">
                  <ReadonlyBox>{row?.pppArrangementId || 'Assigned automatically on save'}</ReadonlyBox>
                </FieldContainer>
              </div>
              <RegistryField
                label="Partner Organisation"
                value={form.partnerOrgUuid}
                onChange={set('partnerOrgUuid')}
                options={organisationOptions}
                placeholder="Select partner organisation"
                fallbackPlaceholder="Organisation UUID"
                onPropose={() => setProposeOrgOpen(true)}
                required
              />
              <SelectField label="PPP Type" value={form.pppType} onChange={set('pppType')} options={PPP_TYPE_OPTIONS} placeholder="Select PPP type" required />
              <SelectField label="Arrangement Model" value={form.arrangementModel} onChange={set('arrangementModel')} options={PPP_ARRANGEMENT_MODEL_OPTIONS} placeholder="Select model" required />
              {isInfrastructure && (
                <RegistryField
                  label="Facility"
                  value={form.facilityUuid}
                  onChange={set('facilityUuid')}
                  options={facilityOptions}
                  placeholder="Select facility"
                  fallbackPlaceholder="Facility UUID"
                  required
                  hint="Shown and mandatory because PPP Type is Infrastructure"
                />
              )}
              <div className="col-span-2">
                <FieldContainer label="Consortium Partners" hint="Optional — select additional partners for consortia arrangements">
                  <MultiSelectPicklist
                    values={form.consortiumPartnerOrgUuids}
                    onChange={(values) => setForm((p) => ({ ...p, consortiumPartnerOrgUuids: values }))}
                    options={organisationOptions}
                    placeholder="Select consortium partners"
                  />
                </FieldContainer>
              </div>
            </div>
          </section>

          <SectionDivider />

          <section className="space-y-3">
            <SectionHead title="Investment" />
            <div className="grid grid-cols-2 gap-4">
              <TextField label="Investment Value (NGN)" type="number" value={form.investmentValueNgn} onChange={set('investmentValueNgn')} placeholder="0" required />
              <TextField label="FMLD Contribution (NGN)" type="number" value={form.fmldContributionNgn} onChange={set('fmldContributionNgn')} placeholder="0" hint="≤ total investment value" />
              <TextField label="Private Contribution (NGN)" type="number" value={form.privateContributionNgn} onChange={set('privateContributionNgn')} placeholder="0" hint="≤ total investment value" />
              <TextField label="Commencement Date" type="date" value={form.commencementDate} onChange={set('commencementDate')} required />
              <TextField label="Duration (months)" type="number" value={form.durationMonths} onChange={set('durationMonths')} placeholder="0" required />
              <TextField label="Expiry Date" type="date" value={form.expiryDate} onChange={set('expiryDate')} required hint="Must be after the Commencement Date" />
            </div>
          </section>

          <SectionDivider />

          <section className="space-y-3">
            <SectionHead title="Geographic Scope & Status" />
            <div className="grid grid-cols-2 gap-4">
              <SelectField label="State" value={form.state} onChange={set('state')} options={stateOptions} placeholder="Select state" required disabled={Boolean(pppLockedState)} />
              <SelectField label="LGA" value={form.lga} onChange={set('lga')} options={lgaOptions} placeholder={loadingLgas ? 'Loading LGAs...' : 'Select LGA (optional)'} disabled={Boolean(pppLockedLga)} />
              <SelectField label="Status" value={form.status} onChange={set('status')} options={PPP_STATUS_OPTIONS} required />
              <div className="col-span-2">
                <FileUploadField
                  label="Agreement Document"
                  value={form.agreementDocument}
                  fileName={form.agreementDocumentFileName}
                  onChange={(dataUri, name) => setForm((p) => ({ ...p, agreementDocument: dataUri, agreementDocumentFileName: name }))}
                  accept=".pdf,image/*"
                  maxSizeMB={10}
                  hint="MoU / contract — PDF, max 10MB"
                />
              </div>
            </div>
          </section>
        </div>
      )}
    </RecordWizard>
    <ProposeOrganisationModal
      isOpen={proposeOrgOpen}
      onClose={() => setProposeOrgOpen(false)}
      onCreated={(org) => {
        const uuid = org?.uuid ?? org?.id ?? '';
        if (!uuid) return;
        setOrganisationOptions((prev) => [
          { value: uuid, label: org?.organisationName ?? org?.organisation_name ?? 'New organisation' },
          ...prev,
        ]);
        setForm((p) => ({ ...p, partnerOrgUuid: uuid }));
      }}
    />
    </>
  );
}

export function CreateNewModal({
  tab,
  onSubmit,
  onClose,
  context = {},
  inline = false,
  modulePanel = null,
  mode = 'create',
  row = null,
  onEdit,
  canEdit,
}) {
  const forms = {
    workplans: WorkplanForm,
    reports: ReportForm,
    ppp: PppForm,
  };

  const Form = forms[tab] ?? WorkplanForm;

  return (
    <ModalOverlay onClose={onClose} inline={inline}>
      <Form
        onSubmit={onSubmit}
        onClose={onClose}
        context={context}
        modulePanel={modulePanel}
        mode={mode}
        row={row}
        onEdit={onEdit}
        canEdit={canEdit}
      />
    </ModalOverlay>
  );
}
